import React, {useState} from 'react';
import {Link, useParams} from 'react-router-dom';
import {useAppDispatch, useAppSelector} from 'store/hooks';
import {enqueueSnackbar} from 'notistack';
import Spinner from 'components/ui/Spinner';
import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import {EditOutlined, UploadFileOutlined} from '@mui/icons-material';
import {a11yProps, TabPanel} from 'layouts/components/Tabs';
import {setSettingTab} from 'store/dashboardSlice';
import Table from 'components/ui/Table';
import Button from 'components/ui/Button';
import TextField from 'components/ui/TextField';
import File from 'components/ui/File';
import CustomerFormModule from './Form';
import {
  ICustomerClasses,
  useGetCustomerClassesQuery,
  useGetCustomerOrdersQuery,
  useGetCustomerQuery
} from 'services/customers';
import {useCreateCertificationMutation, useUpdateCertificationMutation} from 'services/certifications';
import {ordersColumns} from './constants';
import * as Styles from 'layouts/PrivateLayout.styles';
import {formatDate, formatTime} from 'utils/intlDate';

const EditCustomerModule = () => {
  const { customerId = '' } = useParams<{ customerId: string }>();
  const { settingTab } = useAppSelector((state) => state.dashboard);
  const dispatch = useAppDispatch();

  const [certificationFile, setCertificationFile] = useState<any>(null);
  const [isUploadingCertFile, setIsUploadingCertFile] = useState<boolean>(false);
  const [selectedClassId, setSelectedClassId] = useState<string>('');
  const [selectedClassLineItemId, setSelectedClassLineItemId] = useState<string>('');
  const [openConfirm, setOpenConfirm] = useState<boolean>(false);
  const [certificationNumber, setCertificationNumber] = useState<string>('');
  const [isReupload, setIsReupload] = useState<boolean>(false);
  const [currentCertificationId, setCurrentCertificationId] = useState<string>('');

  // New state to manage multiple certification edits

  const [createCertification] = useCreateCertificationMutation();
  const [updateCertification] = useUpdateCertificationMutation();

  const { data: customerData, isLoading, isFetching } = useGetCustomerQuery(customerId);
  const { data: orders, isLoading: isLoadingOrders, isFetching: isFetchingOrders } = useGetCustomerOrdersQuery(customerId);
  const { data: classes, isLoading: isLoadingClasses, isFetching: isFetchingClasses, refetch: refetchClasses } = useGetCustomerClassesQuery(customerId);

  const handleChangeTab = (_event: React.SyntheticEvent, newValue: number) => {
    dispatch(setSettingTab(newValue));
  };

  const handleCloseConfirm = () => {
    setOpenConfirm(false);
    setCertificationFile(null);
    setIsReupload(false);
    setCurrentCertificationId('');
    setCertificationNumber('');
  };

  const handleUploadCertificationFile = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsUploadingCertFile(true);

    try {
      if (isReupload) {
        const updateFormData = new FormData();
        updateFormData.append('certificate_image', certificationFile);
        updateFormData.append('certification_number', certificationNumber);

        await updateCertification({
          certificationId: currentCertificationId,
          body: updateFormData 
        });
        enqueueSnackbar('Certification successfully updated', { variant: 'success', autoHideDuration: 1000 });
      } else {
        const createFormData = new FormData();
        createFormData.append('certificate_image', certificationFile);
        createFormData.append('certification_number', certificationNumber);
        createFormData.append('class_id', selectedClassId);
        createFormData.append('customer_id', customerId);
        createFormData.append('order_line_item_id', selectedClassLineItemId);

        await createCertification(createFormData);
        enqueueSnackbar('Certification successfully created', { variant: 'success', autoHideDuration: 1000 });
      }
      await refetchClasses();
    } catch (error) {
      enqueueSnackbar(`Failed to ${isReupload ? 'update' : 'create'} certification. Please try again.`, { variant: 'error', autoHideDuration: 1000 });
    }

    setIsUploadingCertFile(false);
    handleCloseConfirm();
  };


  return (
      <>
        <Styles.CreateEditPageTitleContainer>
          Edit Customer
        </Styles.CreateEditPageTitleContainer>
        {!isFetching && !isLoading ? (
            <>
              <CustomerFormModule
                  editCustomer={true}
                  customer={customerData?.customer}
              />

              <div className="mt-8">
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                  <Tabs
                      value={settingTab}
                      onChange={handleChangeTab}
                      aria-label="Settings tabs"
                  >
                    <Tab label="Orders" {...a11yProps(0)} />
                  </Tabs>
                </Box>
                <TabPanel value={settingTab} index={0}>
                  <Table
                      columns={ordersColumns}
                      rows={orders?.customer_orders || []}
                      emptyTitleText="No orders yet"
                      emptyText=""
                      isFetching={isFetchingOrders || isLoadingOrders}
                  />
                </TabPanel>
              </div>
              <Dialog
                  open={openConfirm}
                  onClose={handleCloseConfirm}
                  aria-labelledby="Upload Certification"
                  fullWidth
                  maxWidth="sm"
              >
                <form onSubmit={handleUploadCertificationFile}>
                  <DialogTitle id={`alert-dialog-title`}>
                    {isReupload ? 'Re-upload certification' : 'Upload certification'}
                  </DialogTitle>
                  <DialogContent>
                    <Styles.FormColumns>
                      <Styles.FormColumnLeftDescription>
                        <strong>Certification ID*</strong>
                      </Styles.FormColumnLeftDescription>
                    </Styles.FormColumns>
                    <TextField
                        cc-width="full"
                        id="certification_number"
                        name="certification_number"
                        value={certificationNumber}
                        onChange={(e) => setCertificationNumber(e.target.value)}
                        className="pr-4"
                        label="Certification Id"
                        required
                    />
                    <Styles.FormColumns>
                      <Styles.FormColumnLeftDescription>
                        <strong>Certification File*</strong>
                      </Styles.FormColumnLeftDescription>
                    </Styles.FormColumns>
                    <File
                        id="certificate_image"
                        file={certificationFile}
                        setFile={setCertificationFile}
                        isLoading={isUploadingCertFile}
                    />
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={handleCloseConfirm}>Cancel</Button>
                    <Button
                        cc-color="purple"
                        type="submit"
                        disabled={!certificationNumber || !certificationFile}
                    >
                      {isReupload ? 'Re-upload' : 'Upload'}
                    </Button>
                  </DialogActions>
                </form>
              </Dialog>
            </>
        ) : (
            <Spinner />
        )}
      </>
  );
};

export default EditCustomerModule;
